import type { Nullable } from '@clearverify/types';

import type { ActiveInactive, SignUpMethod, VerifierType, VerifierTypeOfGovernment } from 'src/api';
import type { AddressFieldsFormValues } from 'src/constants/address';
import type { SelectFieldOption } from 'src/types/components';
import type { DroppedFile } from 'src/types/file';

// InitialStepForm

export enum InitialStepFormFields {
  firstName = 'firstName',
  lastName = 'lastName',
  email = 'email',
  phoneNumber = 'phoneNumber',
  password = 'password',
  passwordConfirm = 'passwordConfirm',
  homeAddress = 'homeAddress',
  isEmployee = 'isEmployee',
}

export type InitialStepFormValues = {
  [InitialStepFormFields.firstName]: string;
  [InitialStepFormFields.lastName]: string;
  [InitialStepFormFields.email]: string;
  [InitialStepFormFields.phoneNumber]: string;
  [InitialStepFormFields.password]: string;
  [InitialStepFormFields.passwordConfirm]: string;
  [InitialStepFormFields.homeAddress]?: AddressFieldsFormValues;
  [InitialStepFormFields.isEmployee]: boolean;
};

// EmployerBasicInfoForm

export enum EmployerBasicInfoFormFields {
  displayName = 'displayName',
  corporateStructure = 'corporateStructure',
  contactEmail = 'contactEmail',
  contactPhoneNumber = 'contactPhoneNumber',
  industry = 'industry',
  taxYearEndDate = 'taxYearEndDate',
  numberOfEmployees = 'numberOfEmployees',
  avatarImageFile = 'avatarImageFile',
  headquarters = 'headquarters',
  disclaimer = 'disclaimer',
  signUpMethod = 'signUpMethod',
}

export type EmployerBasicInfoFormValues = {
  [EmployerBasicInfoFormFields.displayName]: string;
  [EmployerBasicInfoFormFields.corporateStructure]: Nullable<string>;
  [EmployerBasicInfoFormFields.contactEmail]: string;
  [EmployerBasicInfoFormFields.contactPhoneNumber]: Nullable<string>;
  [EmployerBasicInfoFormFields.industry]: Nullable<SelectFieldOption>;
  [EmployerBasicInfoFormFields.taxYearEndDate]: Nullable<string>;
  [EmployerBasicInfoFormFields.numberOfEmployees]: Nullable<number>;
  [EmployerBasicInfoFormFields.avatarImageFile]: Nullable<DroppedFile>;
  [EmployerBasicInfoFormFields.headquarters]: AddressFieldsFormValues;
  [EmployerBasicInfoFormFields.disclaimer]: string;
  [EmployerBasicInfoFormFields.signUpMethod]: SignUpMethod;
};

// EmployeeVerificationPreferenceForm

export enum EmployeeVerificationPreferenceFormFields {
  isAutoVerificationRequestEnabled = 'isAutoVerificationRequestEnabled',
  signUpMethod = 'signUpMethod',
}

export type EmployeeVerificationPreferenceFormValues = {
  [EmployeeVerificationPreferenceFormFields.isAutoVerificationRequestEnabled]:
    | 'required'
    | 'optional';

  [EmployeeVerificationPreferenceFormFields.signUpMethod]: Nullable<SignUpMethod>;
};

// NoticeToFurnishersForm

export enum NoticeToFurnishersFormFields {
  isRulesRead = 'isRulesRead',
}

export type NoticeToFurnishersFormValues = {
  [NoticeToFurnishersFormFields.isRulesRead]: boolean;
};

export type EmployerFormValues = InitialStepFormValues &
  EmployerBasicInfoFormValues &
  EmployeeVerificationPreferenceFormValues;

// VerifierAccountTypeForm

export enum VerifierAccountTypeFormFields {
  accountType = 'accountType',
  permissiblePurposeReferenceId = 'permissiblePurposeReferenceId',
  isRulesRead = 'isRulesRead',
}

export type VerifierAccountTypeFormValues = {
  [VerifierAccountTypeFormFields.accountType]: VerifierType;
  [VerifierAccountTypeFormFields.permissiblePurposeReferenceId]: string;
  [VerifierAccountTypeFormFields.isRulesRead]: boolean;
};

// VerifierBasicInfoForm

export enum VerifierBasicInfoFormFields {
  accountType = 'accountType',
  permissiblePurposeReferenceId = 'permissiblePurposeReferenceId',
  companyDisplayInfo = 'companyDisplayInfo',
  primaryContactEmail = 'primaryContactEmail',
  primaryContactPhone = 'primaryContactPhone',
  fein = 'fein',
  commercialName = 'commercialName',
  street = 'street',
  city = 'city',
  county = 'county',
  state = 'state',
  zipCode = 'zipCode',
  industry = 'industry',
  websiteUrl = 'websiteUrl',
  avatarImageFile = 'avatarImageFile',
}

export enum CommercialVerifierBasicInfoFormFields {
  registeredBusinessName = 'registeredBusinessName',
}

export enum GovernmentVerifierBasicInfoFormFields {
  governmentEntityName = 'governmentEntityName',
  typeOfGovernment = 'typeOfGovernment',
  governmentDepartment = 'governmentDepartment',
}

export type VerifierBasicInfoFormValues = {
  [VerifierBasicInfoFormFields.accountType]: VerifierType;
  [VerifierBasicInfoFormFields.permissiblePurposeReferenceId]: string;
  [VerifierBasicInfoFormFields.companyDisplayInfo]: string;
  [VerifierBasicInfoFormFields.primaryContactEmail]: string;
  [VerifierBasicInfoFormFields.primaryContactPhone]: string;
  [VerifierBasicInfoFormFields.fein]: string;
  [VerifierBasicInfoFormFields.commercialName]: string;
  [VerifierBasicInfoFormFields.industry]: SelectFieldOption;
  [VerifierBasicInfoFormFields.websiteUrl]: string;
  [VerifierBasicInfoFormFields.avatarImageFile]: Nullable<DroppedFile>;
} & AddressFieldsFormValues;

export type CommercialVerifierBasicInfoFormValues = {
  [CommercialVerifierBasicInfoFormFields.registeredBusinessName]: string;
};

export type GovernmentVerifierBasicInfoFormValues = {
  [GovernmentVerifierBasicInfoFormFields.governmentEntityName]: string;
  [GovernmentVerifierBasicInfoFormFields.typeOfGovernment]: VerifierTypeOfGovernment;
  [GovernmentVerifierBasicInfoFormFields.governmentDepartment]: string;
};

export type VerifierFormValues = InitialStepFormValues &
  VerifierAccountTypeFormValues &
  VerifierBasicInfoFormValues;

export enum PEOBasicInfoFormFields {
  displayName = 'displayName',
  corporateStructure = 'corporateStructure',
  contactEmail = 'contactEmail',
  contactPhoneNumber = 'contactPhoneNumber',
  industry = 'industry',
  taxYearEndDate = 'taxYearEndDate',
  numberOfEmployees = 'numberOfEmployees',
  fein = 'fein',
  avatarImageFile = 'avatarImageFile',
  headquarters = 'headquarters',
  disclaimer = 'disclaimer',
  signUpMethod = 'signUpMethod',
}

export type PEOBasicInfoFormValues = {
  [PEOBasicInfoFormFields.displayName]: string;
  [PEOBasicInfoFormFields.corporateStructure]: Nullable<string>;
  [PEOBasicInfoFormFields.contactEmail]: string;
  [PEOBasicInfoFormFields.contactPhoneNumber]: Nullable<string>;
  [PEOBasicInfoFormFields.industry]: Nullable<SelectFieldOption>;
  [PEOBasicInfoFormFields.taxYearEndDate]: Nullable<string>;
  [PEOBasicInfoFormFields.numberOfEmployees]: Nullable<number>;
  [PEOBasicInfoFormFields.fein]: string;
  [PEOBasicInfoFormFields.avatarImageFile]: Nullable<DroppedFile>;
  [PEOBasicInfoFormFields.headquarters]: AddressFieldsFormValues;
  [PEOBasicInfoFormFields.disclaimer]: string;
  [PEOBasicInfoFormFields.signUpMethod]: SignUpMethod;
};

export enum PEOPayrollProviderBasicInfoFormFields {
  displayName = 'displayName',
  corporateStructure = 'corporateStructure',
  contactEmail = 'contactEmail',
  contactPhoneNumber = 'contactPhoneNumber',
  industry = 'industry',
  taxYearEndDate = 'taxYearEndDate',
  numberOfEmployees = 'numberOfEmployees',
  fein = 'fein',
  avatarImageFile = 'avatarImageFile',
  headquarters = 'headquarters',
  disclaimer = 'disclaimer',
  signUpMethod = 'signUpMethod',
}

export type PEOPayrollProviderBasicInfoFormValues = {
  [PEOPayrollProviderBasicInfoFormFields.displayName]: string;
  [PEOPayrollProviderBasicInfoFormFields.corporateStructure]: Nullable<string>;
  [PEOPayrollProviderBasicInfoFormFields.contactEmail]: string;
  [PEOPayrollProviderBasicInfoFormFields.contactPhoneNumber]: Nullable<string>;
  [PEOPayrollProviderBasicInfoFormFields.industry]: Nullable<SelectFieldOption>;
  [PEOPayrollProviderBasicInfoFormFields.taxYearEndDate]: Nullable<string>;
  [PEOPayrollProviderBasicInfoFormFields.numberOfEmployees]: Nullable<number>;
  [PEOPayrollProviderBasicInfoFormFields.fein]: string;
  [PEOPayrollProviderBasicInfoFormFields.avatarImageFile]: Nullable<DroppedFile>;
  [PEOPayrollProviderBasicInfoFormFields.headquarters]: AddressFieldsFormValues;
  [PEOPayrollProviderBasicInfoFormFields.disclaimer]: string;
  [PEOPayrollProviderBasicInfoFormFields.signUpMethod]: SignUpMethod;
};

// EmployeeSignupMethodForm

export enum EmployeeSignupMethodFormFields {
  signUpMethod = 'signUpMethod',
}

export type EmployeeSignupMethodFormValues = {
  [EmployeeSignupMethodFormFields.signUpMethod]: SignUpMethod;
};

// ConsumerVerificationPreferenceForm

export enum ConsumerVerificationPreferenceFormFields {
  isAutoVerificationRequestEnabled = 'isAutoVerificationRequestEnabled',
}

export type ConsumerVerificationPreferenceFormValues = {
  [ConsumerVerificationPreferenceFormFields.isAutoVerificationRequestEnabled]: ActiveInactive;
};
